import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { calculateGrade, generateId } from '../../utils/helpers';
import { api } from '../../utils/api';

function MarksEntry() {
  const { exams, examTimetable, marks, setMarks, students, classes, subjects, academicYear, teacherMapping, loadTeacherMapping } = useApp();
  const { currentUser } = useAuth();
  const toast = useToast();
  const isAdmin = currentUser?.role === 'admin';
  const isTeacher = currentUser?.role === 'teacher';

  useEffect(() => {
    if (isTeacher) loadTeacherMapping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeacher]);

  // Same pair-aware mapping as Homework (AddHomework.js/HomeworkPage.js) —
  // a class teacher gets full access to their own class; a subject teacher
  // is restricted to the exact class-subject pairs they're assigned, not
  // independent class/subject set membership (which would let a teacher
  // assigned Math/Class 5 + English/Class 6 also enter English marks for
  // Class 5).
  const isClassTeacher = isTeacher && teacherMapping.isClassTeacher;
  const myPairs = teacherMapping.classSubjectPairs || [];
  // Union, not either/or — a class teacher who is also a subject teacher
  // elsewhere needs both their homeroom and those classes available here.
  const availableClasses = isAdmin
    ? classes
    : classes.filter(c =>
        (isClassTeacher && c.id === teacherMapping.classId) ||
        (teacherMapping.assignedClassIds || []).includes(c.id));

  const [selectedExam, setSelectedExam] = useState(exams[0]?.id || '');
  const [selectedClass, setSelectedClass] = useState(availableClasses[0]?.id || '');
  // localMarks[studentId][subjectId] = { marks, remarks }
  const [localMarks, setLocalMarks] = useState({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Subjects come from what's actually been scheduled for this exam+class in
  // the Exam Schedule (exam_timetable) — that's the real source of truth for
  // "which subjects are being examined here", not Subject.classIds. Each
  // exam schedule entry also carries its own maxMarks (exams don't all carry
  // equal weight — a slip test may be out of 20 while an annual exam is out
  // of 100), which overrides the subject's fixed default here.
  const scheduledEntries = (examTimetable || [])
    .filter(e => e.examId === selectedExam && (e.classIds || []).includes(selectedClass));
  const scheduledSubjectIds = new Set(scheduledEntries.map(e => e.subjectId));
  // An exam+subject pair should resolve to exactly one max-marks value; if a
  // stale duplicate schedule entry still exists, prefer the one with the
  // newest (timestamp-prefixed) id so this stays consistent with the
  // server's GET /api/marks override. A schedule entry can also carry a
  // per-class override on top of its own maxMarks (e.g. Nursery out of 20,
  // Class 8 out of 100 in the same shared exam session) — that takes
  // priority for whichever class is currently selected.
  const entryMaxMarksBySubject = {};
  [...scheduledEntries].sort((a, b) => String(a.id).localeCompare(String(b.id)))
    .forEach(e => {
      const classOverride = e.classMaxMarks?.[selectedClass];
      entryMaxMarksBySubject[e.subjectId] = classOverride ?? e.maxMarks;
    });
  const pairedSubjectIds = new Set(myPairs.filter(p => p.classId === selectedClass).map(p => p.subjectId));
  // The class-teacher "every subject" bypass only applies to their own
  // homeroom class specifically — not globally, or a class teacher of one
  // class would wrongly get every subject in any other class they teach too.
  const availableSubjects = subjects
    .filter(s => scheduledSubjectIds.has(s.id))
    .filter(s => isAdmin || (isClassTeacher && selectedClass === teacherMapping.classId) || pairedSubjectIds.has(s.id))
    .map(s => ({ ...s, maxMarks: entryMaxMarksBySubject[s.id] ?? s.maxMarks ?? 100 }));

  const classStudents = students.filter(s => s.classId === selectedClass && s.status === 'active');

  React.useEffect(() => {
    if (!selectedExam || !selectedClass) return;
    const existing = marks.filter(m => m.examId === selectedExam && classStudents.some(s => s.id === m.studentId));
    const map = {};
    existing.forEach(m => {
      const subj = availableSubjects.find(s => s.id === m.subjectId);
      const grade = calculateGrade(m.marksObtained, subj?.maxMarks || 100);
      if (!map[m.studentId]) map[m.studentId] = {};
      map[m.studentId][m.subjectId] = { marks: m.marksObtained, remarks: grade.comment };
    });
    setLocalMarks(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExam, selectedClass]);

  const updateMark = (studentId, subjectId, value) => {
    const subject = availableSubjects.find(s => s.id === subjectId);
    const maxMarks = subject?.maxMarks || 100;
    const comment = value !== '' ? calculateGrade(Number(value), maxMarks).comment : '';
    setLocalMarks(prev => ({
      ...prev,
      [studentId]: { ...(prev[studentId] || {}), [subjectId]: { marks: value, remarks: comment } },
    }));
  };

  const save = async () => {
    if (!selectedExam || !selectedClass) return;
    const examYear = exams.find(e => e.id === selectedExam)?.academicYear;
    const studentIds = classStudents.map(s => s.id);
    const newMarks = [];
    for (const student of classStudents) {
      for (const subject of availableSubjects) {
        const entry = localMarks[student.id]?.[subject.id];
        if (!entry || entry.marks === '' || entry.marks === undefined) continue;
        newMarks.push({
          id: generateId('MRK'), examId: selectedExam, studentId: student.id, subjectId: subject.id,
          marksObtained: Number(entry.marks || 0), maxMarks: subject.maxMarks || 100,
          remarks: entry.remarks || '', enteredBy: currentUser?.profileId || 'STF-001',
          academicYear: examYear || academicYear || '',
        });
      }
    }
    const subjectIds = new Set(availableSubjects.map(s => s.id));
    const filtered = marks.filter(m => !(m.examId === selectedExam && subjectIds.has(m.subjectId) && studentIds.includes(m.studentId)));
    setSaving(true);
    try {
      await api.post('/marks/bulk', { records: newMarks });
      setMarks([...filtered, ...newMarks]);
      toast?.success('Marks saved');
    } catch (e) {
      toast?.error(`Save failed: ${e.message}`);
      setSaving(false);
      return;
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Marks Entry</h1></div>
        <button onClick={save} disabled={!selectedExam||!selectedClass||saving}
          className={`flex items-center gap-2 px-4 py-1 rounded-lg text-sm font-medium disabled:opacity-40 transition-colors ${saved?'bg-green-600 text-white':'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>
          <Save size={16}/>{saving?'Saving...':saved?'Saved!':'Save Marks'}
        </button>
      </div>
      <div className="flex gap-4 mb-6 flex-wrap">
        <div>
          <label className="block font-medium mb-1">Exam</label>
          <select value={selectedExam} onChange={e=>setSelectedExam(e.target.value)} className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
            {exams.map(item=>(<option key={item.id} value={item.id}>{item.name}</option>))}
          </select>
        </div>
        <div>
          <label className="block font-medium mb-1">Class</label>
          <select value={selectedClass} onChange={e=>setSelectedClass(e.target.value)} className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
            <option value="">Select Class</option>
            {availableClasses.map(item=>(<option key={item.id} value={item.id}>{item.name} - Sec {item.section}</option>))}
          </select>
        </div>
      </div>
      {selectedExam && selectedClass && (
        availableSubjects.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center text-gray-400">
            No subjects have been scheduled for this exam in this class yet.<br/>
            Add them first in Exam Schedule{isTeacher ? ' (subjects assigned to you)' : ''}, then come back here.
          </div>
        ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-1 text-left font-semibold sticky left-0 bg-gray-50">#</th>
                <th className="px-4 py-1 text-left font-semibold sticky left-8 bg-gray-50">Student</th>
                {availableSubjects.map(subject => (
                  <th key={subject.id} className="px-3 py-1 text-center font-semibold whitespace-nowrap">
                    {subject.name}<br/><span className="font-normal text-gray-400">(/{subject.maxMarks||100})</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {classStudents.map((student,i)=>(
                <tr key={student.id} className="hover:bg-gray-50">
                  <td className="px-4 py-1 sticky left-0 bg-white">{i+1}</td>
                  <td className="px-4 py-1 font-medium sticky left-8 bg-white whitespace-nowrap">{student.firstName} {student.lastName}</td>
                  {availableSubjects.map(subject => {
                    const marksVal = localMarks[student.id]?.[subject.id]?.marks ?? '';
                    const grade = marksVal !== '' ? calculateGrade(Number(marksVal), subject.maxMarks||100) : null;
                    return (
                      <td key={subject.id} className="px-2 py-1 text-center">
                        <div className="flex flex-col items-center gap-0.5">
                          <input type="number" min="0" max={subject.maxMarks||100} value={marksVal}
                            onChange={e=>updateMark(student.id,subject.id,e.target.value)}
                            className="w-16 text-center px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                          {grade && <span className={`text-xs font-bold ${grade.color}`}>{grade.grade}</span>}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )
      )}
    </div>
  );
}
export default MarksEntry;
